import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { getArticleBySlug, articles } from '@/mocks/articles';

const colorMap: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-800',
  secondary: 'bg-secondary-100 text-secondary-800',
  accent: 'bg-accent-100 text-accent-800',
};

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="font-heading text-2xl text-foreground-950">文章唔存在</h2>
            <button
              type="button"
              onClick={() => navigate('/blog')}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回網誌
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const currentIndex = articles.findIndex((a) => a.slug === slug);
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />
      <main>
        {/* Hero cover */}
        <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top saturate-[0.9] brightness-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent"></div>
        </div>

        <section className="relative -mt-20 md:-mt-28 pb-16 md:pb-24">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <button
              type="button"
              onClick={() => navigate('/blog')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-sm font-medium text-foreground-600 transition-colors cursor-pointer whitespace-nowrap mb-8"
            >
              <i className="ri-arrow-left-line"></i>
              返回網誌
            </button>

            <div className="flex items-center gap-3 mb-6">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${colorMap[article.categoryColor] || 'bg-background-200 text-foreground-700'}`}>
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.subtitle}
            </p>

            {/* Divider */}
            <div className="my-10 flex items-center gap-3">
              <div className="flex-1 h-px bg-background-200"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-primary-400/40"></div>
              <div className="flex-1 h-px bg-background-200"></div>
            </div>

            {/* Article body */}
            <div className="space-y-6">
              {article.content.map((paragraph, idx) => (
                <p key={idx} className="text-sm md:text-base text-foreground-800 leading-[1.8]">
                  {paragraph}
                </p>
              ))}

              {/* Book highlights for article 3 */}
              {article.books && article.books.length > 0 && (
                <div className="mt-10 space-y-8">
                  {article.books.map((book, idx) => (
                    <div
                      key={idx}
                      className="rounded-2xl bg-background-100 border border-background-200/70 overflow-hidden"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] md:grid-cols-[180px_1fr] gap-0">
                        <div className="relative h-48 sm:h-auto overflow-hidden bg-background-200/50">
                          <img
                            src={book.image}
                            alt={book.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="p-6 md:p-8">
                          <div className="text-[10px] tracking-[0.2em] uppercase text-foreground-400 font-semibold mb-1">
                            推薦書目 {idx + 1}
                          </div>
                          <h4 className="font-heading text-lg md:text-xl font-medium text-foreground-950">
                            {book.title}
                          </h4>
                          <p className="text-xs text-foreground-500 mt-0.5">{book.author}</p>
                          <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                            {book.desc}
                          </p>
                          <div className="mt-4 rounded-xl bg-secondary-50 border border-secondary-100/70 p-4">
                            <p className="font-heading text-sm md:text-base leading-relaxed text-foreground-800 italic">
                              {book.quote}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="my-12 flex items-center gap-3">
              <div className="flex-1 h-px bg-background-200"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-primary-400/40"></div>
              <div className="flex-1 h-px bg-background-200"></div>
            </div>

            {/* Prev / Next navigation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {prevArticle && (
                <button
                  type="button"
                  onClick={() => navigate(`/blog/${prevArticle.slug}`)}
                  className="text-left rounded-2xl bg-background-100 border border-background-200/50 p-5 transition-all duration-200 hover:bg-background-200/60 cursor-pointer"
                >
                  <div className="text-[10px] tracking-[0.2em] uppercase text-foreground-400 font-semibold mb-1">
                    上一篇
                  </div>
                  <p className="text-sm font-medium text-foreground-800 line-clamp-2">
                    {prevArticle.title}
                  </p>
                </button>
              )}
              {nextArticle && (
                <button
                  type="button"
                  onClick={() => navigate(`/blog/${nextArticle.slug}`)}
                  className="text-right rounded-2xl bg-background-100 border border-background-200/50 p-5 transition-all duration-200 hover:bg-background-200/60 cursor-pointer sm:ml-auto"
                >
                  <div className="text-[10px] tracking-[0.2em] uppercase text-foreground-400 font-semibold mb-1">
                    下一篇
                  </div>
                  <p className="text-sm font-medium text-foreground-800 line-clamp-2">
                    {nextArticle.title}
                  </p>
                </button>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}